/**
 * EmbeddingStatusService — BlockSuite EmbeddingStatusProvider implementation (sl-amc0, sl-00ro)
 *
 * SOLID: D — implements BlockSuite interface with nbstore events + GraphQL
 *
 * sl-00ro: Events come through the nbstore WebSocket chain (no separate socket).
 * GraphQL is still used for request/response methods (hasEmbeddings, reindex).
 */
import {
  defaultWorkspaceApiClient,
  workspaceApiFetch,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { EmbeddingService } from '@affine/core/modules/workspace-indexer-embedding';
import { I18n } from '@affine/i18n';
import {
  DATABASE_FILE_CELL_INDEX_REVIEW_COPY_EVENT,
  type DatabaseFileCellIndexReviewCopyEventDetail,
  EmbeddingCreditsRequiredError,
  type EmbeddingDocStatusEvent,
  type EmbeddingIndexReviewField,
  type EmbeddingIndexReviewLocator,
  type EmbeddingIndexReviewRegenerableField,
  type EmbeddingIndexReviewRegenerationResult,
  type EmbeddingIndexReviewSubjectSummary,
  EmbeddingStatusExtension,
  type EmbeddingStatusLabelKey,
  EmbeddingStatusUnavailableError,
  type EmbeddingWorkspaceProgressEvent,
  type IEmbeddingStatusService,
} from '@blocksuite/affine/shared/services';
import type { ExtensionType } from '@blocksuite/affine/store';
import type { FrameworkProvider } from '@toeverything/infra';

// ===========================================================================
// Extension Factory
// ===========================================================================

const labelI18nKeys: Record<EmbeddingStatusLabelKey, string> = {
  indexedForSearch: 'com.streamlain.embedding.status.indexedForSearch',
  clickToIndexForSearch: 'com.streamlain.embedding.status.clickToIndex',
  indexingInProgress: 'com.streamlain.embedding.status.indexingInProgress',
  indexingDocuments: 'com.streamlain.embedding.status.indexingDocuments',
  indexingStarted: 'com.streamlain.embedding.status.indexingStarted',
  indexingFailed: 'com.streamlain.embedding.status.indexingFailed',
  indexingFailedHint: 'com.streamlain.embedding.status.indexingFailedHint',
  indexedSuccessfully: 'com.streamlain.embedding.status.indexedSuccessfully',
  reindex: 'com.streamlain.embedding.status.reindex',
  checkAgain: 'com.streamlain.embedding.status.checkAgain',
  reviewDetails: 'com.streamlain.embedding.status.reviewDetails',
  searchIndex: 'com.streamlain.embedding.status.searchIndex',
  indexStatusIndexed: 'com.streamlain.embedding.status.indexStatusIndexed',
  indexStatusNotIndexed:
    'com.streamlain.embedding.status.indexStatusNotIndexed',
  indexStatusIndexing: 'com.streamlain.embedding.status.indexStatusIndexing',
  indexStatusFailed: 'com.streamlain.embedding.status.indexStatusFailed',
  indexStatusDisabled: 'com.streamlain.embedding.status.indexStatusDisabled',
  indexStatusUnavailable:
    'com.streamlain.embedding.status.indexStatusUnavailable',
  indexStatusUnavailableHint:
    'com.streamlain.embedding.status.indexStatusUnavailableHint',
  indexingPaused: 'com.streamlain.embedding.status.indexingPaused',
  indexingPausedHint: 'com.streamlain.embedding.status.indexingPausedHint',
  imageAnalysisPaused: 'com.streamlain.embedding.status.imageAnalysisPaused',
  imageAnalysisPausedHint:
    'com.streamlain.embedding.status.imageAnalysisPausedHint',
  imageAnalysisFailed: 'com.streamlain.embedding.status.imageAnalysisFailed',
  imageAnalysisFailedHint:
    'com.streamlain.embedding.status.imageAnalysisFailedHint',
  existingIndexPreserved:
    'com.streamlain.embedding.status.existingIndexPreserved',
  manageCredits: 'com.streamlain.embedding.status.manageCredits',
  reviewSource: 'com.streamlain.embedding.status.reviewSource',
  reviewSourceGenerated:
    'com.streamlain.embedding.status.reviewSourceGenerated',
  reviewSourceReviewed: 'com.streamlain.embedding.status.reviewSourceReviewed',
  reviewSourceEdited: 'com.streamlain.embedding.status.reviewSourceEdited',
  reviewSourceStale: 'com.streamlain.embedding.status.reviewSourceStale',
  reviewPanelTitle: 'com.streamlain.embedding.status.reviewPanelTitle',
  reviewPanelClose: 'com.streamlain.embedding.status.reviewPanelClose',
  reviewSectionDescription:
    'com.streamlain.embedding.status.reviewSectionDescription',
  reviewSectionSearchTerms:
    'com.streamlain.embedding.status.reviewSectionSearchTerms',
  reviewSectionExtractedText:
    'com.streamlain.embedding.status.reviewSectionExtractedText',
  reviewSectionManualAdditions:
    'com.streamlain.embedding.status.reviewSectionManualAdditions',
  reviewGeneratedValue: 'com.streamlain.embedding.status.reviewGeneratedValue',
  reviewUserValue: 'com.streamlain.embedding.status.reviewUserValue',
  reviewNoValue: 'com.streamlain.embedding.status.reviewNoValue',
  reviewSave: 'com.streamlain.embedding.status.reviewSave',
  reviewSaving: 'com.streamlain.embedding.status.reviewSaving',
  reviewSaved: 'com.streamlain.embedding.status.reviewSaved',
  reviewCancel: 'com.streamlain.embedding.status.reviewCancel',
  reviewRegenerateWithHint:
    'com.streamlain.embedding.status.reviewRegenerateWithHint',
  reviewRegenerateAction:
    'com.streamlain.embedding.status.reviewRegenerateAction',
  reviewRegenerating: 'com.streamlain.embedding.status.reviewRegenerating',
  reviewHintPlaceholder:
    'com.streamlain.embedding.status.reviewHintPlaceholder',
  reviewReplaceWarning: 'com.streamlain.embedding.status.reviewReplaceWarning',
  reviewReadOnly: 'com.streamlain.embedding.status.reviewReadOnly',
  reviewLoadFailed: 'com.streamlain.embedding.status.reviewLoadFailed',
  reviewConflict: 'com.streamlain.embedding.status.reviewConflict',
  reviewQueued: 'com.streamlain.embedding.status.reviewQueued',
  notSearchable: 'com.streamlain.embedding.status.notSearchable',
  notIndexedHint: 'com.streamlain.embedding.status.notIndexedHint',
  embeddingDisabledTitle: 'com.streamlain.embedding.status.disabledTitle',
  embeddingDisabledHint: 'com.streamlain.embedding.status.disabledHint',
};

const INDEX_REVIEW_COPY_MAX_ATTEMPTS = 3;

type EmbeddingIndexReviewRegenerationResponse =
  | EmbeddingIndexReviewRegenerationResult
  | {
      queued: false;
      unavailable: {
        code: 'INDEX_REVIEW_REGENERATION_UNAVAILABLE';
        reason:
          | 'image-ai-unavailable'
          | 'ocr-ai-unavailable'
          | 'text-ai-unavailable';
        message: string;
      };
    };

class IndexReviewHttpError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

export function patchEmbeddingStatusExtension(
  framework: FrameworkProvider
): ExtensionType {
  const workspaceService = framework.get(WorkspaceService);
  const workspaceDialogService = framework.get(WorkspaceDialogService);
  const workspaceServerService = framework.get(WorkspaceServerService);
  const serverBaseUrl =
    workspaceServerService.server?.baseUrl ?? location.origin;
  const workspaceId = workspaceService.workspace.id;

  // Local cache for hasEmbeddings() results
  const cache = new Map<string, { indexed: boolean; ts: number }>();

  // Current workspace progress state
  let currentProgress: EmbeddingWorkspaceProgressEvent | null = null;

  // Subscription cleanup functions
  const disposables: (() => void)[] = [];

  // Subscribe to nbstore embedding events via WorkspaceEngine
  const engine = workspaceService.workspace.engine;

  const docStatus$ = engine.embeddingDocStatus$;
  if (docStatus$) {
    const sub = docStatus$.subscribe(event => {
      if (event.status === 'indexed') {
        cache.set(event.docId, { indexed: true, ts: Date.now() });
      } else if (event.status === 'failed') {
        cache.delete(event.docId);
      }
      for (const listener of docStatusListeners) {
        listener(event);
      }
    });
    disposables.push(() => sub.unsubscribe());
  }

  const progress$ = engine.embeddingWorkspaceProgress$;
  if (progress$) {
    const sub = progress$.subscribe(event => {
      currentProgress = event;
      if (event.docsIndexed >= event.docsTotal) {
        currentProgress = null;
      }
      for (const listener of progressListeners) {
        listener(event);
      }
    });
    disposables.push(() => sub.unsubscribe());
  }

  // Listener sets for the IEmbeddingStatusService callbacks
  const docStatusListeners = new Set<
    (event: EmbeddingDocStatusEvent) => void
  >();
  const progressListeners = new Set<
    (event: EmbeddingWorkspaceProgressEvent) => void
  >();
  const enabledListeners = new Set<(enabled: boolean | null) => void>();

  // Workspace embedding policy (sl: badge-uniformity): badges respect whether
  // semantic indexing is enabled for the workspace. Unknown (null) is treated
  // as enabled by consumers so a slow/failed policy fetch never hides a badge.
  const embeddingService = framework.getOptional(EmbeddingService);
  let currentEnabled: boolean | null =
    embeddingService?.embeddingEnabled.enabled$.value ?? null;
  if (embeddingService) {
    embeddingService.embeddingEnabled.getEnabled();
    const sub = embeddingService.embeddingEnabled.enabled$.subscribe(value => {
      currentEnabled = value;
      for (const listener of enabledListeners) {
        listener(value);
      }
    });
    disposables.push(() => sub.unsubscribe());
  }

  async function gqlFetch<TData = unknown>(
    operation: 'query' | 'mutation',
    query: string,
    variables: Record<string, unknown>
  ): Promise<{ errors?: Array<{ message?: string }>; data?: TData }> {
    const json = await defaultWorkspaceApiClient.requestGraphQL<{
      errors?: Array<{ message?: string }>;
      data?: TData;
    }>({
      workspaceId,
      baseUrl: serverBaseUrl,
      query,
      variables,
      operation,
      category: 'metadata',
      mode: 'background',
      priority: 'background',
    });
    if (json.errors?.length) {
      throw new Error(json.errors[0]?.message ?? 'GraphQL fetch failed');
    }
    return json;
  }

  function indexReviewSubjectUrl(locator: EmbeddingIndexReviewLocator) {
    const url = new URL(
      `${serverBaseUrl}/api/workspace/${locator.workspaceId}/index-review/subject`
    );
    const params: Record<string, string | number | null | undefined> = {
      docId: locator.docId,
      subjectKind: locator.subjectKind,
      blockId: locator.blockId,
      databaseId: locator.databaseId,
      rowId: locator.rowId,
      propertyId: locator.propertyId,
      refId: locator.refId,
      blobId: locator.blobId,
      pageIndex: locator.pageIndex,
    };
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
    return url;
  }

  async function indexReviewFetch<T>(
    path: string,
    init: RequestInit
  ): Promise<T> {
    const foreground = init.method !== undefined && init.method !== 'GET';
    const res = await workspaceApiFetch(
      path,
      { credentials: 'include', ...init },
      {
        workspaceId: inferIndexReviewWorkspaceId(path) ?? workspaceId,
        category: foreground ? 'foreground-action' : 'metadata',
        key: `index-review:${init.method ?? 'GET'}:${path}`,
        mode: foreground ? 'foreground' : 'background',
        priority: foreground ? 'foreground' : 'background',
      }
    );
    if (!res.ok) {
      throw new IndexReviewHttpError(
        `Index review request failed: ${res.status}`,
        res.status
      );
    }
    return (await res.json()) as T;
  }

  function inferIndexReviewWorkspaceId(path: string): string | null {
    const match = path.match(/\/api\/workspace\/([^/?#]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }

  async function flushIndexReviewCopyDocs(
    sourceLocator: EmbeddingIndexReviewLocator,
    targetLocator: EmbeddingIndexReviewLocator
  ): Promise<void> {
    const docEngine = workspaceService.workspace.engine?.doc;
    const flushAndWaitForSynced = docEngine?.flushAndWaitForSynced;
    if (!flushAndWaitForSynced) return;

    for (const docId of uniqueStrings([
      sourceLocator.docId,
      targetLocator.docId,
    ])) {
      docEngine.addDoc?.(docId, 'embedding-review');
      await flushAndWaitForSynced.call(docEngine, docId);
    }
  }

  async function retryIndexReviewCopy<T>(
    task: () => Promise<T>,
    beforeAttempt: () => Promise<void>
  ): Promise<T> {
    let lastError: unknown;
    for (
      let attempt = 1;
      attempt <= INDEX_REVIEW_COPY_MAX_ATTEMPTS;
      attempt++
    ) {
      await beforeAttempt();
      try {
        return await task();
      } catch (error) {
        lastError = error;
        if (
          attempt >= INDEX_REVIEW_COPY_MAX_ATTEMPTS ||
          !isRetryableIndexReviewCopyError(error)
        ) {
          throw error;
        }
        await sleep(attempt * 150);
      }
    }
    throw lastError;
  }

  const service: IEmbeddingStatusService = {
    async hasEmbeddings(docId: string, blockId?: string): Promise<boolean> {
      // Freshness depends on the latest persisted doc snapshot. Do not serve
      // stale positive cache entries after images/attachments are added.
      let data: {
        data?: { workspace?: { hasDocEmbeddings?: unknown } };
      };
      try {
        data = await gqlFetch<{
          workspace?: { hasDocEmbeddings?: unknown };
        }>(
          'query',
          `query HasDocEmbeddings($workspaceId: String!, $docId: String!, $blockId: String) {
              workspace(id: $workspaceId) {
                hasDocEmbeddings(docId: $docId, blockId: $blockId)
              }
            }`,
          { workspaceId, docId, blockId: blockId ?? null }
        );
      } catch (error) {
        throw new EmbeddingStatusUnavailableError(
          error instanceof Error ? error.message : undefined
        );
      }
      const indexed = data?.data?.workspace?.hasDocEmbeddings;
      if (typeof indexed !== 'boolean') {
        throw new EmbeddingStatusUnavailableError(
          'Embedding status response is invalid'
        );
      }
      cache.set(blockId ? `${docId}:${blockId}` : docId, {
        indexed,
        ts: Date.now(),
      });
      return indexed;
    },

    async reindex(
      docId: string,
      locator?: EmbeddingIndexReviewLocator | null
    ): Promise<void> {
      if (locator?.subjectKind === 'image-block') {
        const summary = await service.getIndexReviewSubject?.(locator);
        if (summary?.imageAnalysis?.availability === 'paused_no_credits') {
          throw new EmbeddingCreditsRequiredError();
        }
        if (summary?.imageAnalysis?.availability === 'unavailable') {
          throw new Error('Image analysis is currently unavailable');
        }
      }
      try {
        await gqlFetch(
          'mutation',
          `mutation QueueReindex($workspaceId: String!, $docId: [String!]!) {
              queueWorkspaceEmbedding(workspaceId: $workspaceId, docId: $docId)
            }`,
          { workspaceId, docId: [docId] }
        );
      } catch (error) {
        throw new EmbeddingStatusUnavailableError(
          error instanceof Error ? error.message : undefined
        );
      }
      cache.delete(docId);
    },

    openCreditSettings(): void {
      workspaceDialogService.open('setting', {
        activeTab: 'workspace:billing',
      });
    },

    async getIndexReviewSubject(
      locator: EmbeddingIndexReviewLocator
    ): Promise<EmbeddingIndexReviewSubjectSummary> {
      return indexReviewFetch<EmbeddingIndexReviewSubjectSummary>(
        indexReviewSubjectUrl(locator).toString(),
        { method: 'GET' }
      );
    },

    async saveIndexReviewField(
      locator: EmbeddingIndexReviewLocator,
      field: EmbeddingIndexReviewField,
      value: unknown,
      options?: { expectedActiveVersionId?: string | null }
    ): Promise<void> {
      await indexReviewFetch<{ version: unknown }>(
        `${serverBaseUrl}/api/workspace/${locator.workspaceId}/index-review/subject`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            locator,
            field,
            value,
            expectedActiveVersionId: options?.expectedActiveVersionId ?? null,
          }),
        }
      );
    },

    async regenerateIndexReviewField(
      locator: EmbeddingIndexReviewLocator,
      field: EmbeddingIndexReviewRegenerableField,
      userHint: string,
      options?: {
        mediaType?: string | null;
        confirmed?: boolean;
        expectedActiveVersionId?: string | null;
      }
    ): Promise<EmbeddingIndexReviewRegenerationResponse> {
      return indexReviewFetch<EmbeddingIndexReviewRegenerationResponse>(
        `${serverBaseUrl}/api/workspace/${locator.workspaceId}/index-review/subject/regenerate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            locator,
            field,
            userHint,
            mediaType: options?.mediaType ?? null,
            confirmed: options?.confirmed === true,
            expectedActiveVersionId: options?.expectedActiveVersionId ?? null,
          }),
        }
      );
    },

    async copyIndexReviewSubject(
      sourceLocator: EmbeddingIndexReviewLocator,
      targetLocator: EmbeddingIndexReviewLocator
    ): Promise<void> {
      await retryIndexReviewCopy(
        () =>
          indexReviewFetch<{ versions: unknown[] }>(
            `${serverBaseUrl}/api/workspace/${targetLocator.workspaceId}/index-review/subject/copy`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sourceLocator, targetLocator }),
            }
          ),
        () => flushIndexReviewCopyDocs(sourceLocator, targetLocator)
      );
    },

    onDocStatus(
      callback: (event: EmbeddingDocStatusEvent) => void
    ): () => void {
      docStatusListeners.add(callback);
      return () => {
        docStatusListeners.delete(callback);
      };
    },

    onWorkspaceProgress(
      callback: (event: EmbeddingWorkspaceProgressEvent) => void
    ): () => void {
      progressListeners.add(callback);
      return () => {
        progressListeners.delete(callback);
      };
    },

    getWorkspaceProgress(): EmbeddingWorkspaceProgressEvent | null {
      return currentProgress;
    },

    isEmbeddingEnabled(): boolean | null {
      return currentEnabled;
    },

    onEnabledChange(callback: (enabled: boolean | null) => void): () => void {
      enabledListeners.add(callback);
      return () => {
        enabledListeners.delete(callback);
      };
    },

    getLabel(
      key: EmbeddingStatusLabelKey,
      params?: Record<string, number | string>
    ): string {
      return I18n.t(labelI18nKeys[key], params);
    },

    dispose(): void {
      for (const dispose of disposables) {
        dispose();
      }
      disposables.length = 0;
      docStatusListeners.clear();
      progressListeners.clear();
      enabledListeners.clear();
      cache.clear();
    },
  };

  function copyDatabaseFileCellPropertyReviewSubjects(
    detail: DatabaseFileCellIndexReviewCopyEventDetail
  ): void {
    if (detail.workspaceId !== workspaceId) return;
    const copyIndexReviewSubject =
      service.copyIndexReviewSubject?.bind(service);
    if (!copyIndexReviewSubject) return;
    for (const row of detail.rows) {
      const locatorBase = {
        workspaceId: detail.workspaceId,
        docId: detail.docId,
        subjectKind: 'database-file-cell',
        databaseId: detail.databaseId,
        rowId: row.rowId,
        refId: row.refId,
        blobId: row.blobId,
        pageIndex: null,
      } satisfies Omit<EmbeddingIndexReviewLocator, 'propertyId'>;
      void copyIndexReviewSubject(
        {
          ...locatorBase,
          propertyId: detail.sourcePropertyId,
        },
        {
          ...locatorBase,
          propertyId: detail.targetPropertyId,
        }
      ).catch((error: unknown) => {
        console.warn(
          'Failed to copy database file property index review metadata',
          error
        );
      });
    }
  }

  function onDatabaseFileCellReviewCopy(event: Event): void {
    const detail = (event as CustomEvent<unknown>).detail;
    if (!isDatabaseFileCellIndexReviewCopyEventDetail(detail)) return;
    copyDatabaseFileCellPropertyReviewSubjects(detail);
  }

  if (typeof window !== 'undefined') {
    window.addEventListener(
      DATABASE_FILE_CELL_INDEX_REVIEW_COPY_EVENT,
      onDatabaseFileCellReviewCopy
    );
    disposables.push(() =>
      window.removeEventListener(
        DATABASE_FILE_CELL_INDEX_REVIEW_COPY_EVENT,
        onDatabaseFileCellReviewCopy
      )
    );
  }

  return EmbeddingStatusExtension(service);
}

function isDatabaseFileCellIndexReviewCopyEventDetail(
  value: unknown
): value is DatabaseFileCellIndexReviewCopyEventDetail {
  if (!value || typeof value !== 'object') return false;
  const detail = value as Partial<DatabaseFileCellIndexReviewCopyEventDetail>;
  return (
    typeof detail.workspaceId === 'string' &&
    typeof detail.docId === 'string' &&
    typeof detail.databaseId === 'string' &&
    typeof detail.sourcePropertyId === 'string' &&
    typeof detail.targetPropertyId === 'string' &&
    Array.isArray(detail.rows) &&
    detail.rows.every(
      row =>
        row &&
        typeof row === 'object' &&
        typeof row.rowId === 'string' &&
        typeof row.refId === 'string' &&
        typeof row.blobId === 'string'
    )
  );
}

function isRetryableIndexReviewCopyError(error: unknown): boolean {
  if (!(error instanceof IndexReviewHttpError)) return true;
  return (
    error.status === 400 ||
    error.status === 404 ||
    error.status === 409 ||
    error.status >= 500
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

function uniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values)];
}
